'use client';

import { type KeyboardEvent, type MouseEvent, useEffect, useLayoutEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    CloudCheck,
    CloudLightning,
    Edit3,
    FileCode,
    FilePlusCorner,
    Info,
    Loader2,
    Trash2,
    UploadCloud,
} from 'lucide-react';
import { type OpeningConfirm, useBoardCodeFileActions } from '@/hooks/feature/boards//useBoardCodeFileActions';
import { type CodeFile } from '@/stores/useBoardEditorFileStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import Header from '@/components/modal/Header';

const formatLastModified = (isoString: string) => {
    if (!isoString || isNaN(new Date(isoString).getTime())) {
        console.warn('[formatLastModified] invalid value:', isoString);
        return '日時不明';
    }
    const d = new Date(isoString);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(
        d.getMinutes(),
    )}:${pad(d.getSeconds())}`;
};

type ConfirmButtonProps = {
    onClick: () => void;
    variant: 'cancel' | 'danger' | 'primary';
    loading?: boolean;
    children: React.ReactNode;
};

const ConfirmButton = ({ onClick, variant, loading, children }: ConfirmButtonProps) => {
    const variantClasses = {
        cancel: 'bg-color-ide-file-cancel-button hover:bg-color-ide-file-cancel-button-hover',
        danger: 'bg-color-ide-file-discard-button hover:bg-color-ide-file-discard-button-hover text-color-white-base',
        primary: 'bg-color-ide-file-save-button hover:bg-color-ide-file-save-button-hover text-color-white-base',
    };
    return (
        <button
            onClick={onClick}
            disabled={loading}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors font-cantarell text-xs ${
                variantClasses[variant]
            } ${loading ? 'opacity-70 cursor-default' : ''}`}
        >
            {loading && (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}>
                    <Loader2 size={12} />
                </motion.div>
            )}
            {children}
        </button>
    );
};

const ConfirmRow = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{ overflow: 'hidden' }}
    >
        <div className="flex flex-col gap-1 pt-4 pb-1">{children}</div>
    </motion.div>
);

type CreateConfirmPanelProps = {
    showCreateConfirm: boolean | 'unsaved';
    errorMessage: any;
    onCancel: () => void;
    onCreate: () => void;
    onSaveAndCreate: () => void;
    onDiscardAndCreate: () => void;
};

const CreateConfirmPanel = ({
    showCreateConfirm,
    errorMessage,
    onCancel,
    onCreate,
    onSaveAndCreate,
    onDiscardAndCreate,
}: CreateConfirmPanelProps) => (
    <AnimatePresence>
        {showCreateConfirm === 'unsaved' && (
            <ConfirmRow>
                <div className="flex flex-col gap-1">
                    <div className="flex justify-center items-center gap-2">
                        <IconAdaptive Icon={Info} baseSize={16} className="text-color-ide-file-discode" />
                        <div className="font-cantarell text-xs text-color-ide-file-discode text-center">
                            編集中のファイルが未保存です
                        </div>
                    </div>
                    <div className="flex justify-center items-center gap-2">
                        <div className="font-cantarell text-xs text-gray-600">新規ファイルを作成しますか？</div>
                    </div>
                </div>
                <div className="flex justify-center items-center gap-2 mt-3">
                    <ConfirmButton onClick={onCancel} variant="cancel">
                        キャンセル
                    </ConfirmButton>
                    <ConfirmButton onClick={onDiscardAndCreate} variant="danger">
                        破棄して作成
                    </ConfirmButton>
                    <ConfirmButton onClick={onSaveAndCreate} variant="primary">
                        保存して作成
                    </ConfirmButton>
                </div>
            </ConfirmRow>
        )}
        {showCreateConfirm === true && (
            <ConfirmRow>
                <div className="flex justify-center items-center gap-2">
                    <div className="font-cantarell text-xs text-gray-600">新規ファイルを作成しますか？</div>
                </div>
                <div className="flex justify-center items-center gap-2 mt-3">
                    <ConfirmButton onClick={onCancel} variant="cancel">
                        キャンセル
                    </ConfirmButton>
                    <ConfirmButton onClick={onCreate} variant="primary">
                        作成
                    </ConfirmButton>
                </div>
            </ConfirmRow>
        )}
    </AnimatePresence>
);

type SaveButtonProps = {
    fileId: string;
    isDirty: boolean;
    isSaving: boolean;
    isSavedVisual: boolean;
    isSaved: boolean;
    onSave: (fileId: string, e: MouseEvent<HTMLButtonElement>) => void;
};

const CurrentFileSaveButton = ({ fileId, isDirty, isSaving, isSavedVisual, isSaved, onSave }: SaveButtonProps) => {
    const bgClass = isDirty
        ? 'bg-color-ide-file-discard-button cursor-pointer'
        : isSaving || isSavedVisual
        ? 'bg-color-ide-file-saved cursor-default'
        : 'bg-gray-400 cursor-default';

    const state = isDirty ? 'dirty' : isSaving ? 'saving' : isSavedVisual ? 'savedVisual' : 'saved';

    const content = {
        dirty: {
            icon: <IconAdaptive Icon={UploadCloud} strokeWidth={2} baseSize={20} className="text-color-white-base" />,
            label: '保存する',
        },
        saving: {
            icon: (
                <IconAdaptive Icon={CloudLightning} strokeWidth={2} baseSize={20} className="text-color-white-base" />
            ),
            label: '保存中...',
        },
        savedVisual: {
            icon: <IconAdaptive Icon={CloudCheck} strokeWidth={2} baseSize={20} className="text-color-white-base" />,
            label: '保存完了',
        },
        saved: {
            icon: <IconAdaptive Icon={CloudCheck} strokeWidth={2} baseSize={20} className="text-color-white-base" />,
            label: '保存済み',
        },
    }[state];

    return (
        <button
            onClick={isDirty ? (e) => onSave(fileId, e) : undefined}
            disabled={!isDirty}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg shrink-0 w-[200px] justify-center transition-colors duration-300 ${bgClass}`}
        >
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={state}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-1.5"
                >
                    {isSaving ? (
                        <motion.div
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            {content.icon}
                        </motion.div>
                    ) : (
                        content.icon
                    )}
                    <span className="font-cantarell text-xs font-medium text-color-white-base">{content.label}</span>
                </motion.div>
            </AnimatePresence>
        </button>
    );
};

type CurrentFileRowProps = {
    file: CodeFile;
    editingId: string | null;
    editingName: string;
    errorMessage: any;
    isCurrentDirty: boolean;
    isCurrentSaving: boolean;
    isCurrentSavedVisual: boolean;
    isCurrentSaved: boolean;
    renameInputRef: (el: HTMLInputElement | null) => void;
    onEditingNameChange: (name: string) => void;
    onRenameKeyDown: (e: KeyboardEvent<HTMLInputElement>, fileId: string) => void;
    onRenameConfirm: (fileId: string) => void;
    onRenameCancel: () => void;
    onStartEditing: () => void;
    onSave: (fileId: string, e: MouseEvent<HTMLButtonElement>) => void;
};

const CurrentFileRow = ({
    file,
    editingId,
    editingName,
    errorMessage,
    isCurrentDirty,
    isCurrentSaving,
    isCurrentSavedVisual,
    isCurrentSaved,
    renameInputRef,
    onEditingNameChange,
    onRenameKeyDown,
    onRenameConfirm,
    onRenameCancel,
    onStartEditing,
    onSave,
}: CurrentFileRowProps) => {
    const isEditing = editingId === file.id;

    if (isEditing) {
        return (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <IconAdaptive
                        Icon={FileCode}
                        strokeWidth={1.5}
                        baseSize={20}
                        breakpoints={{ md: 20 }}
                        className="text-color-co-gray-dark"
                    />
                    <input
                        ref={renameInputRef}
                        type="text"
                        value={editingName}
                        maxLength={30}
                        onChange={(e) => onEditingNameChange(e.target.value)}
                        onKeyDown={(e) => onRenameKeyDown(e, file.id)}
                        className="flex-1 font-cantarell text-sm outline-none bg-color-white-base text-gray-800 px-3 py-2 rounded border-2 border-cyan-500 focus:border-cyan-600"
                        placeholder="ファイル名を入力"
                    />
                </div>
                <div className="flex flex-col gap-2 py-1">
                    <div className="flex justify-center items-center gap-2">
                        <div className="font-cantarell text-xs text-gray-600">
                            ファイル名を「{editingName || file.name}」に変更しますか？
                        </div>
                    </div>
                    <div className="flex justify-center items-center mt-2">
                        <ErrorBox message={errorMessage?.message} errors={errorMessage?.errors} />
                    </div>
                    <div className="flex justify-center items-center gap-2 mt-1">
                        <ConfirmButton onClick={onRenameCancel} variant="cancel">
                            キャンセル
                        </ConfirmButton>
                        <ConfirmButton onClick={() => onRenameConfirm(file.id)} variant="primary">
                            変更
                        </ConfirmButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 shrink-0">
                    <motion.div
                        className="w-1.5 h-1.5 rounded-full bg-cyan-500"
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="font-corp font-medium text-xs text-cyan-600 tracking-wide">編集中</span>
                    <div className="w-px h-3 bg-cyan-200 mx-0.5" />
                </div>
                <IconAdaptive
                    Icon={FileCode}
                    strokeWidth={1.5}
                    baseSize={18}
                    breakpoints={{ md: 18 }}
                    className="text-cyan-600 shrink-0"
                />
                <div className="font-cantarell text-sm text-gray-800 truncate">{file.name}</div>
                <button
                    onClick={onStartEditing}
                    className="p-1.5 hover:bg-cyan-100 rounded-md transition-all shrink-0"
                    title="名前を変更"
                >
                    <IconAdaptive Icon={Edit3} strokeWidth={2} baseSize={16} className="text-cyan-600 shrink-0" />
                </button>
            </div>
            {(isCurrentDirty || isCurrentSaving || isCurrentSavedVisual || isCurrentSaved) && (
                <CurrentFileSaveButton
                    fileId={file.id}
                    isDirty={isCurrentDirty}
                    isSaving={isCurrentSaving}
                    isSavedVisual={isCurrentSavedVisual}
                    isSaved={isCurrentSaved}
                    onSave={onSave}
                />
            )}
        </div>
    );
};

type OpeningConfirmPanelProps = {
    file: CodeFile;
    openingConfirm: OpeningConfirm;
    openingAction: 'open' | 'discard' | 'save' | null;
    onCancel: () => void;
    onOpen: (fileId: string) => void;
    onSaveAndOpen: () => void;
    onDiscardAndOpen: () => void;
};

const OpeningConfirmPanel = ({
    file,
    openingConfirm,
    openingAction,
    onCancel,
    onOpen,
    onSaveAndOpen,
    onDiscardAndOpen,
}: OpeningConfirmPanelProps) => (
    <ConfirmRow>
        <div className="flex flex-col gap-1">
            {openingConfirm.hasUnsaved && (
                <div className="flex justify-center items-center gap-2">
                    <IconAdaptive Icon={Info} baseSize={16} className="text-color-ide-file-discode" />
                    <div className="font-cantarell text-xs text-color-ide-file-discode text-center">
                        編集中のファイルが未保存です
                    </div>
                </div>
            )}
            <div className="flex justify-center items-center gap-2">
                <div className="font-cantarell text-xs text-gray-600">{file.name} を開きますか？</div>
            </div>
        </div>
        <div className="flex justify-center items-center gap-2 mt-3 mb-3">
            <ConfirmButton onClick={onCancel} variant="cancel" loading={false}>
                キャンセル
            </ConfirmButton>
            {openingConfirm.hasUnsaved ? (
                <>
                    <ConfirmButton onClick={onDiscardAndOpen} variant="danger" loading={openingAction === 'discard'}>
                        破棄して開く
                    </ConfirmButton>
                    <ConfirmButton onClick={onSaveAndOpen} variant="primary" loading={openingAction === 'save'}>
                        保存して開く
                    </ConfirmButton>
                </>
            ) : (
                <ConfirmButton onClick={() => onOpen(file.id)} variant="primary" loading={openingAction === 'open'}>
                    開く
                </ConfirmButton>
            )}
        </div>
    </ConfirmRow>
);

type FileListItemProps = {
    file: CodeFile;
    editingId: string | null;
    editingName: string;
    deletingId: string | null;
    openingConfirm: OpeningConfirm | null;
    openingAction: 'open' | 'discard' | 'save' | null; // 追加
    errorMessage: any;
    renameInputRef: (el: HTMLInputElement | null) => void;
    onEditingNameChange: (name: string) => void;
    onRenameKeyDown: (e: KeyboardEvent<HTMLInputElement>, fileId: string) => void;
    onRenameConfirm: (fileId: string) => void;
    onRenameCancel: () => void;
    onRenameClick: (file: CodeFile, e: MouseEvent<HTMLButtonElement>) => void;
    onFileClick: (fileId: string) => void;
    onDeleteClick: (fileId: string, e: MouseEvent<HTMLButtonElement>) => void;
    onDeleteConfirm: (fileId: string) => void;
    onDeleteCancel: () => void;
    onOpenCancel: () => void;
    onOpenConfirm: (fileId: string) => void;
    onOpenSaveAndProceed: () => void;
    onOpenDiscardAndProceed: () => void;
};

const FileListItem = ({
    file,
    editingId,
    editingName,
    deletingId,
    openingConfirm,
    openingAction, // 追加
    errorMessage,
    renameInputRef,
    onEditingNameChange,
    onRenameKeyDown,
    onRenameConfirm,
    onRenameCancel,
    onRenameClick,
    onFileClick,
    onDeleteClick,
    onDeleteConfirm,
    onDeleteCancel,
    onOpenCancel,
    onOpenConfirm,
    onOpenSaveAndProceed,
    onOpenDiscardAndProceed,
}: FileListItemProps) => {
    const isEditing = editingId === file.id;
    const isDeleting = deletingId === file.id;
    const isOpenConfirm = openingConfirm?.fileId === file.id;

    const rowBg = isDeleting
        ? 'bg-gray-100'
        : isEditing
        ? 'bg-cyan-50 border-l-2 border-l-cyan-400'
        : isOpenConfirm
        ? 'bg-cyan-50/40'
        : 'hover:bg-gray-50';

    return (
        <motion.div
            key={file.id}
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, x: -20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="w-full overflow-hidden border-b border-gray-100 last:border-b-0"
        >
            <div
                className={`flex items-center justify-between px-3 transition-colors group cursor-pointer ${rowBg}`}
                style={{ height: '40px', flexShrink: 0 }}
                onClick={() => !isEditing && !isDeleting && onFileClick(file.id)}
            >
                <div className="flex items-center gap-2 flex-1">
                    {isEditing ? (
                        <div className="flex items-center gap-2 w-full">
                            <IconAdaptive
                                Icon={FileCode}
                                strokeWidth={1.5}
                                baseSize={18}
                                breakpoints={{ md: 18 }}
                                className="text-color-co-gray-dark"
                            />
                            <input
                                ref={renameInputRef}
                                type="text"
                                value={editingName}
                                maxLength={30}
                                onChange={(e) => onEditingNameChange(e.target.value)}
                                onKeyDown={(e) => onRenameKeyDown(e, file.id)}
                                className="flex-1 font-cantarell text-xs outline-none bg-color-white-base px-2 py-1 rounded border-2 border-cyan-500 focus:border-cyan-600"
                            />
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 flex-1">
                            <IconAdaptive
                                Icon={FileCode}
                                strokeWidth={1.5}
                                baseSize={18}
                                breakpoints={{ md: 18 }}
                                className="text-color-co-gray-dark"
                            />
                            <div className="font-cantarell text-xs text-gray-700">{file.name}</div>
                        </div>
                    )}
                </div>

                {!isEditing && (
                    <div className="flex items-center gap-1">
                        <div className="font-cantarell text-[12px] text-gray-500 mr-2">
                            {formatLastModified(file.lastModified)}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onRenameClick(file, e);
                            }}
                            disabled={isDeleting}
                            className={`p-1 rounded transition-all ${
                                isDeleting ? 'opacity-30 pointer-events-none' : 'hover:bg-gray-200'
                            }`}
                            title="名前を変更"
                        >
                            <Edit3 size={14} className="text-gray-600" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick(file.id, e);
                            }}
                            disabled={isDeleting}
                            className={`p-1 rounded transition-all ${
                                isDeleting ? 'opacity-30 pointer-events-none' : 'hover:bg-red-100'
                            }`}
                            title="削除"
                        >
                            <Trash2 size={14} className="text-red-600" />
                        </button>
                    </div>
                )}
            </div>
            <AnimatePresence>
                {isDeleting && (
                    <ConfirmRow>
                        <div className="flex justify-center items-center gap-2">
                            <div className="font-cantarell text-xs text-gray-600">{file.name} を削除しますか？</div>
                        </div>
                        <div className="flex justify-center items-center">
                            <ErrorBox message={errorMessage?.message} errors={errorMessage?.errors} />
                        </div>
                        <div className="flex justify-center items-center gap-2 mt-1">
                            <ConfirmButton onClick={onDeleteCancel} variant="cancel">
                                キャンセル
                            </ConfirmButton>
                            <ConfirmButton onClick={() => onDeleteConfirm(file.id)} variant="danger">
                                削除
                            </ConfirmButton>
                        </div>
                    </ConfirmRow>
                )}
                {isEditing && (
                    <ConfirmRow>
                        <div className="flex justify-center items-center gap-2">
                            <div className="font-cantarell text-xs text-gray-600">
                                ファイル名を「{editingName || file.name}」に変更しますか？
                            </div>
                        </div>
                        <div className="flex justify-center items-center mt-4">
                            <ErrorBox message={errorMessage?.message} errors={errorMessage?.errors} />
                        </div>
                        <div className="flex justify-center items-center gap-2 mt-3">
                            <ConfirmButton onClick={onRenameCancel} variant="cancel">
                                キャンセル
                            </ConfirmButton>
                            <ConfirmButton onClick={() => onRenameConfirm(file.id)} variant="primary">
                                変更
                            </ConfirmButton>
                        </div>
                    </ConfirmRow>
                )}
                {isOpenConfirm && (
                    <OpeningConfirmPanel
                        file={file}
                        openingConfirm={openingConfirm!}
                        openingAction={openingAction}
                        onCancel={onOpenCancel}
                        onOpen={onOpenConfirm}
                        onSaveAndOpen={onOpenSaveAndProceed}
                        onDiscardAndOpen={onOpenDiscardAndProceed}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

type BoardCodeFileListProps = {
    onClose?: () => void;
};

export default function BoardCodeFileList({ onClose }: BoardCodeFileListProps) {
    const actions = useBoardCodeFileActions(onClose);
    const renameInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    useEffect(() => {
        actions.loadFiles();
    }, []);

    useLayoutEffect(() => {
        const handler = (e: PromiseRejectionEvent) => {
            if (e.reason?.type === 'cancelation' && e.reason?.msg === 'operation is manually canceled') {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener('unhandledrejection', handler, { capture: true });
        return () => window.removeEventListener('unhandledrejection', handler, { capture: true });
    }, []);

    const handleRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>, fileId: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (actions.editingName.trim()) actions.handleRenameConfirm(fileId);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            actions.handleRenameCancel();
        }
    };

    const otherFiles = actions.files.filter((f) => f.id !== actions.currentFileId);

    return (
        <div className="w-full h-full overflow-y-auto p-4">
            <div className="flex flex-row w-full h-12">
                <div className="flex justify-end w-3/4">
                    <Header title="BLOCK CODE FILE" subTitle="" />
                </div>
            </div>

            {actions.isLoading ? (
                <div className="flex justify-center items-center w-full py-4">
                    <ImageAdaptive src="/images/common/loading-circle.svg" baseWidth={32} breakpoints={{ md: 32 }} />
                </div>
            ) : (
                <>
                    <div className="flex flex-col font-corp font-medium text-xs px-2 pt-1 pb-2 w-full gap-2">
                        <div className="flex justify-center">
                            <ButtonGAdaptive
                                label="ブロックコードファイルの新規作成"
                                description="create new file"
                                icon={FilePlusCorner}
                                size="xl"
                                disabled={!!actions.showCreateConfirm}
                                onClick={actions.handleNewFile}
                            />
                        </div>
                        {!actions.editingId && !actions.deletingId && !actions.showCreateConfirm && (
                            <div className="flex justify-center items-center mt-2">
                                <ErrorBox
                                    message={actions.errorMessage?.message}
                                    errors={actions.errorMessage?.errors}
                                />
                            </div>
                        )}
                        <CreateConfirmPanel
                            showCreateConfirm={actions.showCreateConfirm}
                            errorMessage={actions.errorMessage}
                            onCancel={actions.handleCreateCancel}
                            onCreate={actions.handleCreateConfirm}
                            onSaveAndCreate={actions.handleCreateSaveAndProceed}
                            onDiscardAndCreate={actions.handleCreateDiscardAndProceed}
                        />
                    </div>

                    <div className="flex flex-col w-full px-2">
                        {actions.currentFileId && actions.currentFile && (
                            <div className="relative mt-6 pb-1">
                                <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                                    <span className="font-cantarell font-bold text-xs text-white">
                                        編集中のファイル
                                    </span>
                                </div>
                                <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                                    <CurrentFileRow
                                        file={actions.currentFile}
                                        editingId={actions.editingId}
                                        editingName={actions.editingName}
                                        errorMessage={actions.errorMessage}
                                        isCurrentDirty={actions.isCurrentDirty}
                                        isCurrentSaving={actions.isCurrentSaving}
                                        isCurrentSavedVisual={actions.isCurrentSavedVisual}
                                        isCurrentSaved={actions.isCurrentSaved}
                                        renameInputRef={(el) => {
                                            renameInputRefs.current[actions.currentFileId!] = el;
                                        }}
                                        onEditingNameChange={actions.setEditingName}
                                        onRenameKeyDown={handleRenameKeyDown}
                                        onRenameConfirm={actions.handleRenameConfirm}
                                        onRenameCancel={actions.handleRenameCancel}
                                        onStartEditing={() => {
                                            actions.clearError();
                                            actions.setEditingId(actions.currentFileId!);
                                            actions.setEditingName(actions.currentFile!.name);
                                        }}
                                        onSave={actions.handleSaveClick}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="relative mt-8 pb-1">
                            <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                                <span className="font-cantarell font-bold text-xs text-white">ファイル一覧</span>
                            </div>
                            <div
                                className="relative rounded-2xl overflow-hidden"
                                style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}
                            >
                                <AnimatePresence mode="popLayout">
                                    {otherFiles.map((file) => (
                                        <FileListItem
                                            key={file.id}
                                            file={file}
                                            editingId={actions.editingId}
                                            editingName={actions.editingName}
                                            deletingId={actions.deletingId}
                                            openingConfirm={actions.openingConfirm}
                                            openingAction={actions.openingAction}
                                            errorMessage={actions.errorMessage}
                                            renameInputRef={(el) => {
                                                renameInputRefs.current[file.id] = el;
                                            }}
                                            onEditingNameChange={actions.setEditingName}
                                            onRenameKeyDown={handleRenameKeyDown}
                                            onRenameConfirm={actions.handleRenameConfirm}
                                            onRenameCancel={actions.handleRenameCancel}
                                            onRenameClick={actions.handleRenameClick}
                                            onFileClick={actions.isLoading ? () => {} : actions.handleFileClick}
                                            onDeleteClick={actions.handleDeleteClick}
                                            onDeleteConfirm={actions.handleDeleteConfirm}
                                            onDeleteCancel={actions.handleDeleteCancel}
                                            onOpenCancel={actions.handleOpenCancel}
                                            onOpenConfirm={actions.handleOpenConfirm}
                                            onOpenSaveAndProceed={actions.handleOpenSaveAndProceed}
                                            onOpenDiscardAndProceed={actions.handleOpenDiscardAndProceed}
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
